/**
 * @fileoverview HTTP Client Service
 * @description Provides HTTP client with authentication and error handling interceptors
 * @module shared/http-client
 */

import { environment } from '@/environments/environment';
import { SecureLogger } from './secure-logger';
import { notifyUnauthorized } from './auth-session';

/**
 * Token Provider Function Type
 * @description Function that retrieves authentication token
 */
export type TokenProvider = () => Promise<string | null>;

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

interface HttpErrorContext {
  status?: number;
  url?: string;
  method?: HttpMethod;
  message: string;
  data?: unknown;
  isNetworkError?: boolean;
}

/**
 * HTTP Client
 * @class HttpClient
 * @description HTTP client with interceptors for authentication and error handling.
 * Implements singleton pattern for consistent request handling across the app.
 * @example
 * ```typescript
 * import { httpClient } from '@/shared/http-client';
 * const data = await httpClient.get<User>('/api/users/123');
 * ```
 */
class HttpClient {
  private tokenProvider: TokenProvider | null = null;
  private readonly baseURL: string;
  private readonly defaultTimeoutMs = 30000;

  /**
   * Create HTTP client instance
   * @param {string} baseURL - Base URL for API requests
   */
  constructor(baseURL: string = environment.apiUrl) {
    this.baseURL = baseURL;
  }

  /**
   * Set token provider function for authentication
   * @param {TokenProvider} provider - Function that retrieves authentication token
   */
  setTokenProvider(provider: TokenProvider): void {
    this.tokenProvider = provider;
  }

  private buildUrl(url: string): string {
    if (/^https?:\/\//i.test(url)) {
      return url;
    }

    const normalizedBaseUrl = this.baseURL.replace(/\/+$/, '');

    if (!url || url.trim() === '') {
      return normalizedBaseUrl;
    }

    const normalizedPath = url.startsWith('/') ? url : `/${url}`;

    return `${normalizedBaseUrl}${normalizedPath}`;
  }

  private async buildHeaders(): Promise<Headers> {
    const headers = new Headers();

    if (this.tokenProvider) {
      try {
        const token = await this.tokenProvider();
        if (token) {
          headers.set('Authorization', `Bearer ${token}`);
        }
      } catch (error) {
        SecureLogger.error('Error getting auth token:', error);
      }
    }

    return headers;
  }

  private async parseResponse<T>(response: Response): Promise<T> {
    if (response.status === 204) {
      return undefined as T;
    }

    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      return (await response.json()) as T;
    }

    const text = await response.text();
    return text as unknown as T;
  }

  /**
   * Handle HTTP errors
   */
  private handleError(error: HttpErrorContext): void {
    const status = error.status ?? 'NO_STATUS';
    const url = error.url || '';
    const message = error.message || 'Unknown error';

    SecureLogger.error('HTTP error', {
      status,
      url,
      message,
      error: error.data,
    });

    if (error.status) {
      switch (error.status) {
        case 401:
          SecureLogger.warn('Unauthorized access - redirecting to login');
          notifyUnauthorized();
          break;
        case 403:
          SecureLogger.warn('Forbidden access');
          break;
        case 404:
          SecureLogger.warn('Resource not found');
          break;
        case 500:
          SecureLogger.error('Internal server error');
          break;
        default:
          SecureLogger.error('Unhandled HTTP error');
      }
    } else if (error.isNetworkError) {
      SecureLogger.error('No response received:', {
        url: error.url,
        method: error.method,
      });
    } else {
      SecureLogger.error('Request setup error:', message);
    }
  }

  private async request<TResponse, TBody = unknown>(
    method: HttpMethod,
    url: string,
    data?: TBody
  ): Promise<TResponse> {
    const controller = new AbortController();
    const timeoutMs = this.defaultTimeoutMs;
    const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

    const headers = await this.buildHeaders();
    const resolvedUrl = this.buildUrl(url);

    let body: BodyInit | undefined;
    if (data !== undefined) {
      if (data instanceof FormData) {
        body = data;
      } else if (data instanceof Blob || data instanceof ArrayBuffer || typeof data === 'string') {
        body = data as BodyInit;
      } else {
        headers.set('Content-Type', 'application/json');
        body = JSON.stringify(data);
      }
    }

    SecureLogger.debug('HTTP Request:', {
      method,
      url: resolvedUrl,
    });

    try {
      const requestInit: RequestInit = {
        method,
        headers,
        signal: controller.signal,
      };

      if (body !== undefined) {
        requestInit.body = body;
      }

      const response = await fetch(resolvedUrl, requestInit);

      SecureLogger.debug('HTTP Response:', {
        status: response.status,
        url: resolvedUrl,
      });

      if (!response.ok) {
        let errorData: unknown;
        try {
          errorData = await this.parseResponse<unknown>(response);
        } catch {
          errorData = undefined;
        }

        this.handleError({
          status: response.status,
          url: resolvedUrl,
          method,
          message: response.statusText || 'HTTP error',
          data: errorData,
        });

        throw new Error(response.statusText || `HTTP ${response.status}`);
      }

      return await this.parseResponse<TResponse>(response);
    } catch (error: unknown) {
      if ((error as Error).name === 'AbortError') {
        this.handleError({
          url: resolvedUrl,
          method,
          message: 'Request timeout',
          isNetworkError: true,
        });
      } else {
        this.handleError({
          url: resolvedUrl,
          method,
          message: (error as Error)?.message || 'Network error',
          isNetworkError: true,
        });
      }
      throw error;
    } finally {
      window.clearTimeout(timeoutId);
    }
  }

  /**
   * GET request
   * @template T - Response data type
   * @param {string} url - Request URL
   * @returns {Promise<T>} Response data
   */
  async get<T>(url: string): Promise<T> {
    return this.request<T>('GET', url);
  }

  /**
   * POST request
   * @template TResponse - Response data type
   * @template TBody - Request body type
   * @param {string} url - Request URL
   * @param {TBody} data - Request body
   * @returns {Promise<TResponse>} Response data
   */
  async post<TResponse, TBody = unknown>(url: string, data?: TBody): Promise<TResponse> {
    return this.request<TResponse, TBody>('POST', url, data);
  }

  /**
   * PUT request
   */
  async put<TResponse, TBody = unknown>(url: string, data?: TBody): Promise<TResponse> {
    return this.request<TResponse, TBody>('PUT', url, data);
  }

  /**
   * DELETE request
   */
  async delete<T>(url: string): Promise<T> {
    return this.request<T>('DELETE', url);
  }

  /**
   * PATCH request
   */
  async patch<TResponse, TBody = unknown>(url: string, data?: TBody): Promise<TResponse> {
    return this.request<TResponse, TBody>('PATCH', url, data);
  }
}

// Export the class for type usage and testing
export { HttpClient };

// Create and export a singleton instance
export const httpClient = new HttpClient();
export default httpClient;
